import React, { useEffect, useState } from 'react';
import { View, Text, ActivityIndicator, StyleSheet } from 'react-native';
import { SplashScreenManager } from '../utils/splashScreenManager';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../store';
import { loadLocationPreferences, loadUIPreferences } from '../store/userSlice';
import { logger } from '../utils/logger';

interface AppInitializerProps {
  children: React.ReactNode;
}

export default function AppInitializer({ children }: AppInitializerProps): React.ReactElement {
  const [isInitialized, setIsInitialized] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    const initializeApp = async (): Promise<void> => {
      try {
        logger.info('[AppInitializer] Starting initialization...');
        
        // Keep splash screen visible during initialization
        await SplashScreenManager.preventAutoHide();

        // Load user preferences
        await Promise.all([
          dispatch(loadLocationPreferences()),
          dispatch(loadUIPreferences())
        ]);

        // Add a small delay to ensure all resources are loaded
        await new Promise(resolve => setTimeout(resolve, 500));

        logger.info('[AppInitializer] Initialization complete');
        setIsInitialized(true);

        // Hide splash screen after initialization
        await SplashScreenManager.hide();
      } catch (error) {
        logger.error('[AppInitializer] Initialization error:', error instanceof Error ? error : new Error(String(error)));
        setInitError(error instanceof Error ? error.message : 'Unknown error');
        // Hide splash screen even on error
        await SplashScreenManager.hide();
      }
    };

    void initializeApp();
  }, [dispatch]);

  if (initError !== null) {
    return (
      <View style={styles.container}>
        <Text style={styles.errorTitle}>Initialization Error</Text>
        <Text style={styles.errorMessage}>{initError}</Text>
      </View>
    );
  }

  if (!isInitialized) {
    return (
      <View style={styles.container}>
        <ActivityIndicator size="large" color="#0066CC" />
        <Text style={styles.loadingText}>Loading...</Text>
      </View>
    );
  }

  return <>{children}</>;
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#fff',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#666',
  },
  errorTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#d32f2f',
    marginBottom: 8,
  },
  errorMessage: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    paddingHorizontal: 32,
  },
});